// One comment thread as a card (#13194, artboards 2 and 4): the quoted text,
// the opening comment, its reactions, the replies, and on the active card the
// reply field. One component for the comments column, the margin card and the
// phone sheet. The agent's half (PR 2): the reply field's "Send to Finpilot"
// (on by default in a thread the agent is in), and the row that says the
// agent is working on a comment sent to it until its reply lands.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import {
  ArrowUp,
  Check,
  CircleCheck,
  CircleHelp,
  Clock,
  Ellipsis,
  Eye,
  RotateCcw,
  SmilePlus,
  Sparkle,
  ThumbsUp,
  Unlink,
  type LucideIcon,
} from "lucide-react";
import { Menu } from "@/components/FpPages/RMS/design";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import { useFlash } from "@/hooks/productTimerHooks";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { WorkingSparkle } from "@/components/common/WorkingSparkle";
import { commentEditedTime, commentTime } from "./commentTime";
import { artifactLink } from "../FpPages/Artifacts/artifactSlug";
import {
  COMMENT_REACTIONS,
  type CommentReaction,
  type CommentReactionEmoji,
  type DocumentComment,
  type DocumentCommentActions,
  type DocumentCommentThread as Thread,
} from "./documentComments";
import {
  AgentMentionChip,
  CommentBodyText,
  SendToAgentCheckbox,
  isSendKey,
  useAgentMention,
  useChipIndent,
} from "./sendToAgent";

/** How long "Link copied" stays on the More button's tooltip after Copy
 *  link is picked from its menu. */
export const COPIED_HINT_MS = 1500;

/** The five reactions as the glyphs the card draws. */
const REACTION_GLYPHS: Record<
  CommentReactionEmoji,
  { icon: LucideIcon; label: string }
> = {
  "👍": { icon: ThumbsUp, label: "Thumbs up" },
  "✅": { icon: Check, label: "Check" },
  "👀": { icon: Eye, label: "Eyes" },
  "❓": { icon: CircleHelp, label: "Question" },
  "⏳": { icon: Clock, label: "Later" },
};

const isReaction = (emoji: string): emoji is CommentReactionEmoji =>
  (COMMENT_REACTIONS as readonly string[]).includes(emoji);

/** The link that opens the document on this thread. */
export const commentLink = (artifactId: string, threadId: string): string =>
  `${artifactLink(artifactId)}?comment=${encodeURIComponent(threadId)}`;

export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

/** The agent's disc: ink, with the spark. */
const AgentDisc: React.FC<{ muted?: boolean }> = ({ muted = false }) => (
  <span
    aria-hidden="true"
    className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[var(--fp-ink)] text-white ${
      muted ? "opacity-70" : ""
    }`}
  >
    <Sparkle className="h-[13px] w-[13px]" fill="currentColor" />
  </span>
);

const AuthorDisc: React.FC<{ comment: DocumentComment; muted: boolean }> = ({
  comment,
  muted,
}) =>
  comment.author_kind === "agent" ? (
    <AgentDisc muted={muted} />
  ) : (
    <span
      aria-hidden="true"
      className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#d8d5cd] text-[10px] font-semibold tracking-[0.02em] text-[var(--fp-ink)]"
    >
      {initialsOf(comment.author_name)}
    </span>
  );

const IconButton: React.FC<{
  label: string;
  hint?: string;
  /** Show the hint now, whatever the pointer and the focus are doing (a
   *  confirmation); otherwise it shows on hover and keyboard focus. */
  showHint?: boolean;
  icon: LucideIcon;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  pressed?: boolean;
}> = ({ label, hint, showHint = false, icon: Icon, onClick, pressed }) => {
  // Hover and keyboard focus, read from the button's own events: the
  // Tooltip's onOpen is not called while it is already open, so a hover or
  // a focus that starts while the hint is forced open would be lost. Hover
  // is a mouse's only: a tap sends a compatibility mouse enter and no leave
  // until the next tap elsewhere, which would leave the tooltip up over the
  // picker or the menu the tap opened.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const open = showHint || hovered || focused;
  const title = hint ?? label;
  // The title the tooltip last showed open stays through its fade-out, so
  // "Link copied" does not turn back into "More" as it leaves.
  const [closingTitle, setClosingTitle] = useState(title);
  useEffect(() => {
    if (open) setClosingTitle(title);
  }, [open, title]);
  return (
    <Tooltip
      title={open ? title : closingTitle}
      arrow
      placement="top"
      describeChild
      open={open}
    >
      <button
        type="button"
        aria-label={label}
        aria-pressed={pressed}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") setHovered(true);
        }}
        onPointerLeave={() => setHovered(false)}
        onFocus={(event) =>
          setFocused(event.currentTarget.matches(":focus-visible"))
        }
        onBlur={() => setFocused(false)}
        onClick={(event) => {
          event.stopPropagation();
          onClick(event);
        }}
        className={`inline-flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-[6px] text-[#5a6068] hover:bg-[var(--fp-bg-well)] hover:text-[var(--fp-ink)] ${
          pressed ? "bg-[var(--fp-bg-well)] text-[var(--fp-ink)]" : ""
        }`}
      >
        <Icon className="h-[14px] w-[14px]" aria-hidden="true" />
      </button>
    </Tooltip>
  );
};

/** Who gave a reaction, in the order they did, the caller (their own user
 *  id) as "You" and first. */
const reactorsLine = (
  reaction: CommentReaction,
  viewerId: string | undefined
): string => {
  const own = reaction.reactors.some((r) => r.user_id === viewerId);
  const others = reaction.reactors
    .filter((r) => r.user_id !== viewerId)
    .map((r) => r.name);
  return (own ? ["You", ...others] : others).join(", ");
};

/** One reaction chip: the glyph and the count, pressed when it is the
 *  caller's. Hovering it with a mouse, or focusing it from the keyboard,
 *  says who reacted; its name says so too. */
const ReactionChip: React.FC<{
  reaction: CommentReaction;
  emoji: CommentReactionEmoji;
  viewerId: string | undefined;
  onToggle: (emoji: CommentReactionEmoji, mine: boolean) => void;
}> = ({ reaction, emoji, viewerId, onToggle }) => {
  const { icon: Icon, label } = REACTION_GLYPHS[emoji];
  const who = reactorsLine(reaction, viewerId);
  // Hover is a mouse's only, and focus counts when it is :focus-visible,
  // as for the card's icon buttons.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <Tooltip
      title={who}
      arrow
      placement="top"
      describeChild
      open={who !== "" && (hovered || focused)}
    >
      <button
        type="button"
        aria-label={
          who
            ? `${label}, ${reaction.count}: ${who}`
            : `${label}, ${reaction.count}`
        }
        aria-pressed={reaction.mine}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") setHovered(true);
        }}
        onPointerLeave={() => setHovered(false)}
        onFocus={(event) =>
          setFocused(event.currentTarget.matches(":focus-visible"))
        }
        onBlur={() => setFocused(false)}
        onClick={(event) => {
          event.stopPropagation();
          onToggle(emoji, reaction.mine);
        }}
        className={`inline-flex h-[22px] cursor-pointer items-center gap-[5px] rounded-[11px] border px-2 text-[12px] ${
          reaction.mine
            ? "border-[#2A3DCB] bg-[#F7F8FF] text-[#2A3DCB]"
            : "border-[var(--fp-border-strong)] bg-[var(--fp-surface)] text-[var(--fp-ink)] hover:border-[var(--fp-ink)]"
        }`}
      >
        <Icon className="h-[13px] w-[13px]" aria-hidden="true" />
        {reaction.count}
      </button>
    </Tooltip>
  );
};

/** The reaction chips under a comment: one per reaction someone gave, the
 *  caller's own pressed; a click toggles the caller's. */
const ReactionChips: React.FC<{
  comment: DocumentComment;
  onToggle: (emoji: CommentReactionEmoji, mine: boolean) => void;
}> = ({ comment, onToggle }) => {
  const { data: viewer } = useCurrentUser();
  const shown = comment.reactions.filter(
    (r) => r.count > 0 && isReaction(r.emoji)
  );
  if (shown.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map((reaction) => (
        <ReactionChip
          key={reaction.emoji}
          reaction={reaction}
          emoji={reaction.emoji as CommentReactionEmoji}
          viewerId={viewer?.user_id}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
};

/** The five-glyph picker under the action row. */
const ReactionPicker: React.FC<{
  onPick: (emoji: CommentReactionEmoji) => void;
  onClose: () => void;
}> = ({ onPick, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);
  return (
    <div
      ref={ref}
      role="group"
      aria-label="Reactions"
      className="absolute right-0 top-7 z-10 flex gap-0.5 rounded-[10px] border border-[var(--fp-border)] bg-[var(--fp-surface)] p-1 [box-shadow:var(--fp-lift)]"
    >
      {COMMENT_REACTIONS.map((emoji) => {
        const { icon: Icon, label } = REACTION_GLYPHS[emoji];
        return (
          <Tooltip key={emoji} title={label} arrow placement="top">
            <button
              type="button"
              aria-label={label}
              onClick={(event) => {
                event.stopPropagation();
                onPick(emoji);
              }}
              className="inline-flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-[7px] text-[var(--fp-ink)] hover:bg-[var(--fp-bg-well)]"
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};

/** A field that takes plain text and posts on Cmd/Ctrl+Enter or its
 *  button. Given the agent's name, it also offers "Send to <name>", checked
 *  from the start when `sendByDefault`. */
const TextField: React.FC<{
  label: string;
  placeholder: string;
  submitLabel: string;
  onSubmit: (text: string, sendToAgent: boolean) => Promise<unknown>;
  sendToAgentAs?: string | null;
  sendByDefault?: boolean;
}> = ({
  label,
  placeholder,
  submitLabel,
  onSubmit,
  sendToAgentAs = null,
  sendByDefault = false,
}) => {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const agent = useAgentMention(sendToAgentAs, sendByDefault);
  const chip = useChipIndent();
  const armed = draft.trim().length > 0 && !sending;
  const send = async () => {
    if (!armed) return;
    setSending(true);
    setFailed(false);
    try {
      await onSubmit(agent.compose(draft), agent.send);
      setDraft("");
      agent.reset();
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      {/* One box: the text on top, and inside the same border the row with
          "Send to <agent>" at the left and the send arrow at the right. */}
      <div
        data-comment-field=""
        className="flex min-w-0 flex-1 flex-col gap-1 rounded-[10px] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] px-2.5 pb-1.5 pt-1.5 focus-within:border-[var(--fp-ink)]"
      >
        <span className="relative flex min-w-0">
          {agent.mention && sendToAgentAs && (
            <span className="absolute left-0 top-[3px]">
              <AgentMentionChip name={sendToAgentAs} chipRef={chip.chipRef} />
            </span>
          )}
          <textarea
            aria-label={label}
            value={draft}
            rows={1}
            placeholder={placeholder}
            style={{ textIndent: agent.mention ? chip.indent : 0 }}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              setDraft(agent.absorb(event.target.value));
              event.target.style.height = "auto";
              event.target.style.height = `${Math.min(event.target.scrollHeight, 160)}px`;
            }}
            onKeyDown={(event) => {
              if (
                event.key === "Backspace" &&
                agent.mention &&
                event.currentTarget.selectionStart === 0 &&
                event.currentTarget.selectionEnd === 0
              ) {
                event.preventDefault();
                agent.dropMention();
              } else if (isSendKey(event)) {
                event.preventDefault();
                void send();
              }
            }}
            className="min-h-[22px] min-w-0 flex-1 resize-none bg-transparent py-0.5 text-[13px] leading-[1.45] text-[var(--fp-ink)] outline-none placeholder:text-[#82878c]"
          />
        </span>
        <div className="flex min-w-0 items-center gap-1.5">
          {sendToAgentAs && (
            <SendToAgentCheckbox
              name={sendToAgentAs}
              checked={agent.send}
              onToggle={agent.toggle}
              disabled={sending}
            />
          )}
          <span className="flex-1" />
          <Tooltip title={submitLabel} arrow placement="top" describeChild>
            <span>
              <button
                type="button"
                aria-label={submitLabel}
                disabled={!armed}
                onClick={(event) => {
                  event.stopPropagation();
                  void send();
                }}
                className="inline-flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-[6px] bg-gray-800 text-white disabled:cursor-default disabled:opacity-30"
              >
                <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          </Tooltip>
        </div>
      </div>
      {failed && (
        <div role="alert" className="text-[12px] text-[var(--fp-danger)]">
          The comment was not saved.
        </div>
      )}
    </div>
  );
};

/** Marks the field a comment is edited in: its own Escape cancels the
 *  edit, so a card around it leaves that key to it. */
export const COMMENT_EDIT_FIELD_ATTR = "data-comment-edit-field";

/** The field a comment is edited in: the whole body, wrapped as the comment
 *  is and growing with it, focused with the caret at its end. Cmd/Ctrl+Enter
 *  or Save saves; Escape or Cancel leaves the comment as it was. While a save
 *  is out, Escape and Cancel wait for it, as the composer's do for a post:
 *  the save lands with the field on screen, or says it failed with the text
 *  kept. */
const CommentEditField: React.FC<{
  initial: string;
  onSave: (body: string) => Promise<unknown>;
  onCancel: () => void;
}> = ({ initial, onSave, onCancel }) => {
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);
  const armed = draft.trim().length > 0 && !saving;
  // As tall as its text, so every line of the comment is in view.
  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    // The height is the border box, so the border goes on top of the text.
    field.style.height = "auto";
    const border = field.offsetHeight - field.clientHeight;
    field.style.height = `${field.scrollHeight + border}px`;
  }, [draft]);
  // An edit usually adds to the end, so the caret starts there.
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }, []);
  const save = async () => {
    if (!armed) return;
    setSaving(true);
    setFailed(false);
    try {
      await onSave(draft);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <textarea
        ref={fieldRef}
        aria-label="Edit comment"
        {...{ [COMMENT_EDIT_FIELD_ATTR]: "" }}
        value={draft}
        rows={1}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (isSendKey(event)) {
            event.preventDefault();
            void save();
          } else if (event.key === "Escape") {
            event.preventDefault();
            if (!saving) onCancel();
          }
        }}
        className="w-full resize-none overflow-hidden whitespace-pre-wrap break-words rounded-[7px] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] px-2.5 py-1.5 text-[13.5px] leading-[1.5] text-[var(--fp-ink)] outline-none focus:border-[var(--fp-ink)]"
      />
      {failed && (
        <div role="alert" className="text-[12px] text-[var(--fp-danger)]">
          The comment was not saved.
        </div>
      )}
      <div className="flex items-center justify-end gap-1">
        <button
          type="button"
          disabled={saving}
          onClick={(event) => {
            event.stopPropagation();
            onCancel();
          }}
          className="h-[28px] cursor-pointer rounded-[7px] px-2 text-[12.5px] font-medium text-[#5a6068] hover:bg-[var(--fp-bg-well)] disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!armed}
          onClick={(event) => {
            event.stopPropagation();
            void save();
          }}
          className="h-[28px] cursor-pointer rounded-[7px] bg-gray-800 px-3 text-[12.5px] font-medium text-white disabled:cursor-default disabled:opacity-40"
        >
          Save
        </button>
      </div>
    </div>
  );
};

/** The agent answering a comment sent to it: the chat's pulsing sparkle
 *  and the line, until its reply takes the row's place. It starts where the
 *  text above it does. */
const AgentWorkingRow: React.FC<{ name: string }> = ({ name }) => (
  <div
    role="status"
    className="flex min-w-0 items-center gap-1.5"
    data-agent-working=""
  >
    <WorkingSparkle size="sm" />
    <span className="min-w-0 truncate text-[13px] text-[#5a6068]">
      {name} is working on it
    </span>
    <span aria-hidden="true" className="flex flex-none gap-[3px]">
      {[0, 1, 2].map((dot) => (
        <span
          key={dot}
          className="h-1 w-1 animate-pulse rounded-full bg-[#82878c]"
          style={{ animationDelay: `${dot * 200}ms` }}
        />
      ))}
    </span>
  </div>
);

/** A comment's More: Copy link, and Edit and Delete on the caller's own
 *  comment. "Link copied" shows on it for COPIED_HINT_MS after a copy. */
const CommentMore: React.FC<{
  mine: boolean;
  /** Copies the thread's link; settles once the copy has (or has not). */
  onCopyLink: () => Promise<unknown>;
  onEdit: () => void;
  onDelete: () => void;
  /** Told whether the button must stay shown: while its menu is open or
   *  its "Link copied" is, so the row holding it does not hide it (the
   *  tooltip would sit at the window's corner) when the pointer has left
   *  the card for the menu. */
  onHoldChange: (held: boolean) => void;
}> = ({ mine, onCopyLink, onEdit, onDelete, onHoldChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // This More's own copy: another More in the thread says nothing.
  const [copied, flashCopied] = useFlash(COPIED_HINT_MS);
  // From the pick until the copy settles, so the row does not drop between
  // the menu closing and "Link copied" showing.
  const [copying, setCopying] = useState(false);
  const held = anchor !== null || copying || copied;
  useEffect(() => {
    onHoldChange(held);
  }, [held, onHoldChange]);
  return (
    <>
      <IconButton
        label="More"
        hint={copied ? "Link copied" : "More"}
        showHint={copied}
        icon={Ellipsis}
        onClick={(event) => setAnchor(event.currentTarget)}
      />
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        // The menu closes itself (onClose) before an item's onSelect.
        onClose={() => setAnchor(null)}
        label="Comment actions"
        items={[
          {
            label: "Copy link",
            onSelect: () => {
              setCopying(true);
              onCopyLink()
                // A failed copy is said by the card's own line (`attempt`).
                .then(flashCopied, () => {})
                .finally(() => setCopying(false));
            },
          },
          // Edit and Delete only on the caller's own comment.
          ...(mine
            ? [
                { label: "Edit", onSelect: onEdit },
                {
                  label: "Delete",
                  destructive: true,
                  onSelect: onDelete,
                },
              ]
            : []),
        ]}
      />
    </>
  );
};

/** One comment: the author row (disc, name, time, and on a reply its More),
 *  the body or its edit field, and the reaction chips. */
const CommentEntry: React.FC<{
  comment: DocumentComment;
  muted: boolean;
  /** The reply's More, at the right of its author row (given the setter
   *  that keeps the row shown while it is held); the opening comment's sits
   *  on the quote line with the thread's actions. */
  more?: (onHoldChange: (held: boolean) => void) => React.ReactNode;
  showActions: boolean;
  editing: boolean;
  onCancelEdit: () => void;
  onSaveEdit: (body: string) => Promise<unknown>;
  onToggleReaction: (emoji: CommentReactionEmoji, mine: boolean) => void;
  /** The document's agent's name: a comment sent to it with its mention
   *  shows the mention as the chip. */
  agentName: string | null;
}> = ({
  comment,
  muted,
  more,
  showActions,
  editing,
  onCancelEdit,
  onSaveEdit,
  onToggleReaction,
  agentName,
}) => {
  const [moreHeld, setMoreHeld] = useState(false);
  return (
    <div className="flex flex-col gap-1.5" data-comment-id={comment.id}>
      <div className="flex min-w-0 items-center gap-1.5">
        <AuthorDisc comment={comment} muted={muted} />
        <span
          className={`min-w-0 truncate text-[13px] font-semibold ${
            muted ? "text-[#5a6068]" : "text-[var(--fp-ink)]"
          }`}
        >
          {comment.author_name}
        </span>
        {/* The time is whole or absent, never cut, and the name comes
            first; with the opening comment's actions on the quote line, the
            row holds both for any name a reply's row holds. The box takes
            what the name leaves (none when the name needs it all, which is
            then ellipsised) and holds one 16px line; a time wider than the
            box wraps to a second line the box clips whole. The first item is
            a 16px-tall strut, so that line ends where the second begins: a
            zero-height first line would leave the wrapped time at the top,
            cut sideways (17d8f5b: "11:45 /"). */}
        <span className="flex h-4 min-w-0 flex-1 basis-0 flex-wrap overflow-hidden">
          <span aria-hidden="true" className="h-4 w-0" />
          <time
            dateTime={comment.created_at}
            className="whitespace-nowrap font-mono text-[11px] leading-4 text-[#6E7480]"
          >
            {commentTime(comment.created_at)}
            {/* The marker says when on hover, like Slack's (Hunter,
                2026-10-12). It is not a Tab stop: focus would scroll the
                clipping box above to reveal a time it holds out of sight,
                leaving the row showing the time cut. */}
            {comment.edited_at && (
              <Tooltip
                title={`Edited ${commentEditedTime(comment.edited_at)}`}
                arrow
                placement="top"
                describeChild
              >
                <span className="cursor-default">{" · edited"}</span>
              </Tooltip>
            )}
          </time>
        </span>
        {/* Shown on hover, on the active card and while held; hidden, it
            takes no room from the name and the time. */}
        {more && (
          <span
            className={`relative flex-none items-center ${
              showActions || moreHeld ? "flex" : "hidden group-hover:flex"
            }`}
          >
            {more(setMoreHeld)}
          </span>
        )}
      </div>
      {editing ? (
        <CommentEditField
          initial={comment.body}
          onSave={onSaveEdit}
          onCancel={onCancelEdit}
        />
      ) : (
        <p
          className={`m-0 whitespace-pre-line break-words text-[13.5px] leading-[1.5] ${
            muted ? "text-[#5a6068]" : "text-[var(--fp-ink)]"
          }`}
        >
          <CommentBodyText
            body={comment.body}
            sentToAgent={comment.sent_to_agent}
            agentName={agentName}
          />
        </p>
      )}
      <ReactionChips comment={comment} onToggle={onToggleReaction} />
    </div>
  );
};

export type CommentCardVariant = "column" | "margin" | "sheet";

const DocumentCommentThread: React.FC<{
  thread: Thread;
  artifactId: string;
  /** Whether the live document still carries the thread's mark. */
  inDocument: boolean;
  active: boolean;
  variant?: CommentCardVariant;
  actions: DocumentCommentActions;
  onActivate: () => void;
  /** The document's agent's name; null when the document has none. */
  agentName?: string | null;
  /** That name when the caller may send the agent a reply. */
  sendToAgentAs?: string | null;
}> = ({
  thread,
  artifactId,
  inDocument,
  active,
  variant = "column",
  actions,
  onActivate,
  agentName = null,
  sendToAgentAs = null,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openingMoreHeld, setOpeningMoreHeld] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<DocumentComment | null>(null);
  // A write or a copy that failed, said in a line on the card (no toast).
  const [failure, setFailure] = useState<string | null>(null);
  const attempt = (work: Promise<unknown>, message: string): void => {
    setFailure(null);
    work.catch(() => setFailure(message));
  };
  const resolved = thread.resolved_at !== null;
  const [opening, ...replies] = thread.comments;
  if (!opening) return null;

  const toggleReaction =
    (comment: DocumentComment) =>
    (emoji: CommentReactionEmoji, mine: boolean) => {
      attempt(
        mine
          ? actions.unreact(thread.id, comment.id, emoji)
          : actions.react(thread.id, comment.id, emoji),
        "The reaction was not saved."
      );
    };

  const pick = (emoji: CommentReactionEmoji) => {
    setPickerOpen(false);
    const already = opening.reactions.find((r) => r.emoji === emoji)?.mine;
    toggleReaction(opening)(emoji, Boolean(already));
  };

  const copyLink = (): Promise<void> => {
    const copy = navigator.clipboard.writeText(
      commentLink(artifactId, thread.id)
    );
    attempt(copy, "The link was not copied.");
    return copy;
  };

  const threadActions = resolved ? (
    <IconButton
      label="Reopen"
      icon={RotateCcw}
      onClick={() =>
        attempt(actions.reopen(thread.id), "The thread was not reopened.")
      }
    />
  ) : (
    <>
      <IconButton
        label="React"
        icon={SmilePlus}
        pressed={pickerOpen}
        onClick={() => setPickerOpen((open) => !open)}
      />
      <IconButton
        label="Resolve"
        icon={CircleCheck}
        onClick={() =>
          attempt(actions.resolve(thread.id), "The thread was not resolved.")
        }
      />
      {pickerOpen && (
        <ReactionPicker onPick={pick} onClose={() => setPickerOpen(false)} />
      )}
    </>
  );

  const more = (
    comment: DocumentComment,
    onHoldChange: (held: boolean) => void
  ) => (
    <CommentMore
      mine={comment.mine}
      onCopyLink={copyLink}
      onEdit={() => setEditing(comment.id)}
      onDelete={() => setDeleting(comment)}
      onHoldChange={onHoldChange}
    />
  );
  // The action row shows on hover, on the active card, while the picker is
  // open or the opening comment's More is held (its menu open or its copy
  // said), and always on the margin card.
  const showActions =
    active || pickerOpen || openingMoreHeld || variant === "margin";

  const entry = (comment: DocumentComment, isOpening: boolean) => (
    <CommentEntry
      key={comment.id}
      comment={comment}
      muted={resolved}
      more={
        isOpening ? undefined : (onHoldChange) => more(comment, onHoldChange)
      }
      showActions={active || pickerOpen}
      editing={editing === comment.id}
      onCancelEdit={() => setEditing(null)}
      onSaveEdit={async (body) => {
        await actions.edit(thread.id, comment.id, body);
        setEditing(null);
      }}
      onToggleReaction={toggleReaction(comment)}
      agentName={agentName}
    />
  );

  const surface =
    variant === "margin"
      ? "border border-[var(--fp-border)] bg-[var(--fp-surface)] [box-shadow:var(--fp-lift)]"
      : active
        ? "border border-[var(--fp-border)] bg-[var(--fp-surface)] [box-shadow:var(--fp-lift)]"
        : resolved
          ? "border border-transparent bg-[#fbfaf8]"
          : "border border-transparent hover:bg-[var(--fp-bg)]";

  return (
    <article
      aria-label={`Comment on ${thread.quote}`}
      data-state={active ? "active" : "idle"}
      data-thread-id={thread.id}
      data-in-document={inDocument ? "true" : "false"}
      data-resolved={resolved ? "true" : "false"}
      onClick={onActivate}
      className={`group relative flex cursor-pointer flex-col gap-2 rounded-[10px] px-3.5 pb-2.5 pt-3 ${
        variant === "margin" ? "w-[236px]" : "w-full"
      } ${surface}`}
    >
      {/* The quote line, and at its right the thread's actions (React,
          Resolve or Reopen, the opening comment's More): one line, the
          quote ellipsised beside them, so the author row below keeps the
          name and the time. */}
      <div className="flex h-6 min-w-0 items-center gap-2">
        <div
          className={`min-w-0 flex-1 truncate border-l-2 pl-2 text-[12.5px] leading-[1.4] ${
            !inDocument
              ? "border-[rgba(23,25,28,0.2)] text-[#82878c] line-through"
              : resolved
                ? "border-[rgba(23,25,28,0.2)] text-[#82878c]"
                : "border-[#E9C96A] text-[#5a6068]"
          }`}
        >
          {thread.quote}
        </div>
        <span
          className={`relative flex-none items-center gap-0.5 ${
            showActions ? "flex" : "hidden group-hover:flex"
          }`}
        >
          {threadActions}
          {more(opening, setOpeningMoreHeld)}
        </span>
      </div>
      {entry(opening, true)}
      {/* Every message on the opening comment's left edge, set apart by
          space rather than an indent; the working row under the last one. */}
      {replies.length > 0 && (
        <div className="flex flex-col gap-3 pt-1">
          {replies.map((reply) => entry(reply, false))}
          {thread.agent_working && !resolved && (
            <AgentWorkingRow name={agentName ?? "Finpilot"} />
          )}
        </div>
      )}
      {replies.length === 0 && thread.agent_working && !resolved && (
        <AgentWorkingRow name={agentName ?? "Finpilot"} />
      )}
      {!inDocument && (
        <div className="flex items-center gap-1.5 text-[12px] text-[#6E7480]">
          <Unlink className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
          This passage is no longer in the document
        </div>
      )}
      {resolved && (
        <div className="flex items-center gap-1.5 text-[12px] text-[#6E7480]">
          <CircleCheck className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
          Resolved
          {thread.resolved_by_name ? ` by ${thread.resolved_by_name}` : ""}
          {thread.resolved_at ? ` · ${commentTime(thread.resolved_at)}` : ""}
        </div>
      )}
      {failure && (
        <div role="alert" className="text-[12px] text-[var(--fp-danger)]">
          {failure}
        </div>
      )}
      {active && !resolved && (
        // On the same edge as the messages above it.
        <div className="mt-1 flex">
          <TextField
            // Keyed by the thread: the agent coming into it (its first reply)
            // keeps a reply being typed; the box takes its default when the
            // field mounts.
            key={thread.id}
            label="Reply"
            placeholder={
              sendToAgentAs && thread.addressed_to_agent
                ? `Ask ${sendToAgentAs} to edit`
                : "Reply…"
            }
            submitLabel="Send"
            sendToAgentAs={sendToAgentAs}
            sendByDefault={thread.addressed_to_agent}
            onSubmit={(body, sendToAgent) =>
              actions.reply(thread.id, body, sendToAgent)
            }
          />
        </div>
      )}
      <ConfirmationModal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          const target = deleting;
          setDeleting(null);
          if (target)
            attempt(
              actions.remove(thread.id, target.id),
              "The comment was not deleted."
            );
        }}
        title="Delete comment?"
        message={
          deleting?.id === opening.id && replies.length > 0
            ? "The replies are deleted with it."
            : undefined
        }
        confirmText="Delete"
        confirmButtonColor="error"
      />
    </article>
  );
};

export default DocumentCommentThread;
